import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	Button,
	Typography,
	Box,
	Stack,
	Chip,
	FormControlLabel,
	Switch,
	useTheme,
} from '@mui/material';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import { CompleteDealPolicySelection } from '@/components/views/DealView/CompletePipeline/CompleteDealPolicySelection';
import { InsightClaimTable } from '@/components/tables/InsightClaimTable';
import { useAddPoliciesToDealMutation, useGetDealPoliciesQuery } from '@/features/deals/dealsApi';
import { showToast } from '@/features/toast/toastSlice';
import { useParams } from 'react-router-dom';
import { SelectPolicyTransactionTable } from '@/components/tables/SelectPolicyTransactionTable';
import { usePoliciesSelect } from '@/context/policiesContext';
import FolioCheckbox from '@/components/FolioCheckbox';

export const EditPolicyTransactionDialog = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const dialog = useSelector((state) => state.dialog);
	const open = dialog?.open?.['editPolicyTransaction'];
	const dialogData = dialog?.data?.['editPolicyTransaction'] ?? {};
	const { dealId } = useParams();

	const { data: policyData, ...policiesQuery } = useGetDealPoliciesQuery(
		{ dealId },
		{ skip: !dealId },
	);
	const policies = policyData?.policies ?? [];

	const { dealRow, insight } = useObjectContext();
	const insightId = insight?.Id;

	const { selectedPolicies, setSelectedPolicies } = usePoliciesSelect();
	const [loading, setLoading] = useState(false);
	const [displayAllTransactions, setDisplayAllTransactions] = useState(false);

	const isNewBusiness = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isRenewal = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_RENEWALS;
	const isEndorsement = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_ENDORSEMENTS;
	const isDebtor = dealRow?.objectType === 'debtor';

	const isPolicyUploadDeal = isNewBusiness || isEndorsement || isRenewal;

	const [savePolicyDocuments, setSavePolicyDocuments] = useState(true);

	const [addPoliciesToDeal] = useAddPoliciesToDealMutation();

	const handleClose = () => {
		setSelectedPolicies([]);
		dispatch(closeDialog('editPolicyTransaction'));
	};

	const policySelectionContent = useMemo(() => {
		switch (dealRow?.pipeline) {
			case process.env.REACT_APP_PIPELINE_CLAIMS:
				return <InsightClaimTable setLoading={setLoading} />;
			case process.env.REACT_APP_PIPELINE_DEBTORS:
				return (
					<>
						<Box mt={3} display='flex' justifyContent='flex-end'>
							<Box>
								<FolioCheckbox
									checked={displayAllTransactions}
									onChange={(e) => setDisplayAllTransactions(e.target.checked)}
									size='small'
									label='Display $0.00 invoices'
								/>
							</Box>
						</Box>
						{selectedPolicies?.length > 0 && (
							<Stack
								direction='row'
								spacing={1}
								flexWrap='wrap'
								sx={{ mb: 1, mt: 3 }}
								alignItems='center'
							>
								<Typography variant='subtitle2'>Selected invoice(s)</Typography>
								{selectedPolicies.map((t) => {
									const id = t.policyTransId ?? t.Id;
									return (
										<Chip
											key={id}
											color='primary'
											label={`Invoice ${t.InvoiceNumber}`}
											onDelete={() => {
												const updated = selectedPolicies.filter(
													(x) => (x.policyTransId ?? x.Id) !== id,
												);
												setSelectedPolicies(updated);
											}}
											size='small'
										/>
									);
								})}
							</Stack>
						)}

						<SelectPolicyTransactionTable
							showDisabledRows={false}
							type={'invoice'}
							clientId={insightId}
							setSelectedTransactions={setSelectedPolicies}
							transactions={selectedPolicies}
							displayAllTransactions={displayAllTransactions}
						/>
					</>
				);
			default:
				return (
					<SelectPolicyTransactionTable
						type={dialogData?.type}
						showDisabledRows={false}
						policyId={dialogData.policy?.Id}
						setSelectedTransactions={setSelectedPolicies}
						transactions={selectedPolicies}
						displayAllTransactions={true}
						clientId={insightId}
					/>
				);
		}
	}, [
		dealRow?.pipeline,
		selectedPolicies,
		policies,
		setSelectedPolicies,
		dialogData,
		displayAllTransactions,
		insightId,
	]);

	const completeLinkTransaction = async () => {
		console.log('selected policies: ', selectedPolicies);

		setLoading(true);
		try {
			const policiesToAdd = selectedPolicies
				.filter((policy) => !policies.some((linkedPolicy) => linkedPolicy.Id === policy.Id))
				.map((policy) => {
					const body = {
						policyId: policy.PolicyId ?? policy.policyId,
					};
					if (dialogData?.type === 'transaction') {
						body.policyTransId = policy.Id ?? policy.policyTransId;
					}
					return body;
				});
			console.log('policies to add: ', policiesToAdd);
			await addPoliciesToDeal({
				dealId,
				policies: policiesToAdd,
				uploadPolicyDocs: isPolicyUploadDeal && savePolicyDocuments,
			});

			handleClose();
			dispatch(
				showToast({
					message: `Successfully linked ${policiesToAdd.length} ${dialogData?.type === 'transaction' ? (isRenewal || isEndorsement ? 'policy transaction' : 'invoice') : 'policy'}${
						policiesToAdd.length > 1 ? 's' : ''
					} to ${dialogData?.type === 'transaction' ? (isDebtor ? 'debtor' : 'deal') : 'deal'}: ${dealRow?.dealName}!`,
					success: true,
				}),
			);
		} catch (err) {
			console.error('Error adding policies', err);
			dispatch(showToast({ message: 'Error adding policies', error: true }));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideBackdrop={true}
			PaperProps={{
				sx: { minWidth: { xs: '100%', sm: '70%', md: '50%' } },
			}}
		>
			<DialogTitle sx={{ fontWeight: 'bold' }}>
				{dialogData?.type === 'transaction'
					? dealRow?.pipeline === process.env.REACT_APP_PIPELINE_DEBTORS
						? 'Add Invoice'
						: 'Policy transaction'
					: 'Add Policy'}
			</DialogTitle>
			<DialogContent>{policySelectionContent}</DialogContent>
			<DialogActions sx={{ p: 0 }}>
				<Stack width='100%'>
					<Box sx={{ px: 3, py: 1 }}>
						{isPolicyUploadDeal && (
							<FormControlLabel
								control={
									<Switch
										checked={savePolicyDocuments}
										disabled={loading}
										onChange={(e) => setSavePolicyDocuments(e.target.checked)}
									/>
								}
								label={'Save policy documents to Sharepoint'}
							/>
						)}
					</Box>

					<Stack
						direction='row'
						sx={{
							backgroundColor: theme.palette.action.hover,
							padding: '1.5em',
							justifyContent: 'flex-end',
						}}
						spacing={1}
					>
						<Button onClick={handleClose} color='primary'>
							Cancel
						</Button>
						<Button
							color='primary'
							variant='contained'
							loading={loading}
							disabled={loading || selectedPolicies.length === 0}
							onClick={completeLinkTransaction}
						>
							Add
						</Button>
					</Stack>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
